import * as React from 'react'
import { CloudArrowUp } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { buttonVariants } from './button'

/**
 * FileDropzone — the drag-&-drop / click-to-browse file input area. A dashed
 * target with an upload glyph, a prompt, a format/size hint, and a "Browse"
 * affordance; highlights while a file is dragged over it.
 *
 * Pair it with <FileCard> to list the files the user picks. This is the input
 * only — it holds no file state; the consumer owns the picked files (via
 * `onFiles`) and renders the list.
 *
 *   size   sm | md   (md is the tall centered target; sm is a compact
 *          single-row strip for inline surfaces like an email compose area)
 *
 * The file <input> is visually hidden but stays in the tab order (sr-only, not
 * `hidden`), and the target shows a focus ring via focus-within — so it's
 * reachable and operable by keyboard, not just mouse drag.
 */

type FileDropzoneSize = 'sm' | 'md'

export interface FileDropzoneProps
  extends Omit<React.LabelHTMLAttributes<HTMLLabelElement>, 'onDrop' | 'title'> {
  /** Called with the picked files (from drop or the file dialog). */
  onFiles?: (files: File[]) => void
  /** `accept` attribute for the underlying <input> (e.g. "image/*,.pdf"). */
  accept?: string
  /** Allow selecting more than one file. */
  multiple?: boolean
  disabled?: boolean
  size?: FileDropzoneSize
  /** Upload glyph shown at the top (defaults to a cloud-upload icon). */
  icon?: React.ReactNode
  /** Primary prompt. */
  title?: React.ReactNode
  /** Secondary helper line (supported formats, size limit, …). */
  hint?: React.ReactNode
  /** Label on the browse affordance. */
  buttonLabel?: React.ReactNode
}

export const FileDropzone = React.forwardRef<HTMLLabelElement, FileDropzoneProps>(
  (
    {
      className,
      onFiles,
      accept,
      multiple = false,
      disabled = false,
      size = 'md',
      icon,
      title = 'Drag & drop or click to upload',
      hint,
      buttonLabel = 'Browse file',
      ...props
    },
    ref
  ) => {
    const [dragActive, setDragActive] = React.useState(false)
    const compact = size === 'sm'

    const emit = (files: FileList | null) => {
      if (!files || files.length === 0) return
      // Enforce `multiple` here so drag-drop matches the file dialog: the <input>
      // constrains the dialog via its `multiple` attr, but a drop can carry
      // several files regardless — cap to the first when multiple is false.
      const list = Array.from(files)
      onFiles?.(multiple ? list : list.slice(0, 1))
    }

    const input = (
      <input
        type="file"
        className="sr-only"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => {
          emit(e.target.files)
          e.target.value = '' // allow re-picking the same file
        }}
      />
    )

    const browse = (
      <span
        className={buttonVariants({
          variant: 'primary',
          appearance: 'outline',
          size: compact ? 'xs' : 'sm',
        })}
      >
        {buttonLabel}
      </span>
    )

    return (
      <label
        ref={ref}
        data-slot="file-dropzone"
        data-drag-active={dragActive || undefined}
        onDragOver={(e) => {
          // Suppress the browser default even when disabled — otherwise dragging
          // a file over the (disabled) zone lets the browser navigate to it on
          // drop. Only the drag-active visual is gated behind `disabled`.
          e.preventDefault()
          if (disabled) return
          setDragActive(true)
        }}
        onDragLeave={(e) => {
          if (disabled) return
          // Only clear when the pointer leaves the dropzone itself — moving
          // between inner children fires dragleave too and would flicker.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null))
            setDragActive(false)
        }}
        onDrop={(e) => {
          e.preventDefault()
          if (disabled) return
          setDragActive(false)
          emit(e.dataTransfer.files)
        }}
        className={cn(
          'flex cursor-pointer rounded-lg border border-dashed transition-colors',
          'focus-within:ring-2 focus-within:ring-fbui-ring-brand',
          // md: a tall centered target. sm: a compact single-row strip.
          compact
            ? 'items-center gap-3 p-3 text-left'
            : 'flex-col items-center p-12 text-center',
          dragActive
            ? 'border-fbui-primary bg-fbui-gray-50'
            : 'border-fbui-gray-300 hover:bg-fbui-gray-50',
          disabled && 'cursor-not-allowed opacity-60 hover:bg-transparent',
          className
        )}
        {...props}
      >
        {compact ? (
          <>
            <span
              aria-hidden="true"
              className="flex shrink-0 text-fbui-gray-400 [&>svg]:size-5"
            >
              {icon ?? <CloudArrowUp />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-fbui-gray-900">
                {title}
              </span>
              {hint != null && (
                <span className="block truncate text-xs text-fbui-gray-500">
                  {hint}
                </span>
              )}
            </span>
            {input}
            {browse}
          </>
        ) : (
          <>
            <span
              aria-hidden="true"
              className="mb-4 flex text-fbui-gray-400 [&>svg]:size-6"
            >
              {icon ?? <CloudArrowUp />}
            </span>
            <span className="mb-1 text-sm font-medium text-fbui-gray-900">
              {title}
            </span>
            {hint != null && (
              <span className="mb-6 text-xs text-fbui-gray-500">{hint}</span>
            )}
            {input}
            <span className={cn(hint == null && 'mt-6')}>{browse}</span>
          </>
        )}
      </label>
    )
  }
)
FileDropzone.displayName = 'FileDropzone'
